// Marketing attribution capture — 1:1 carryover from `ls-app`
// (`client/src/lib/utils.ts` lines ~137-274 + `pages/register-page.tsx`
// lines ~60-90), restructured for the Cortex stack.
//
// What this module does:
//   - Reads the `leadscraper_ft` cookie set by the Webflow landing pages and
//     parses it into a structured shape (utm_*, gclid, fbclid, msclkid, etc.).
//   - Reads the `ls_offer` cookie + `?source` / `?offer` URL params used by
//     the Beta-Coupon flow.
//   - Captures URL `utm_*` params and merges them into the structured
//     attribution (URL wins over cookie for explicit current-visit overrides
//     only when the cookie is missing the key).
//
// The result is sent to `POST /api/auth/signup` as
//   `attribution: { ft, structured, source }`
// and persisted on the UserDoc by the backend.
//
// SSR-safe: every function returns the empty/null variant when `document`
// or `window` is undefined.

import { resolveOffer } from '@cortex/schemas';

// Janik 04.10.2026: fresh direct signups use PRISM, matching the marketing site.
// Explicit campaign links and existing offer assignments keep their precedence.
const DEFAULT_SIGNUP_OFFER = 'prism';

export interface AttributionData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  oppref?: string; // OpenAI Ads: opaque click identifier, preserve unchanged.
  ttclid?: string;
  li_fat_id?: string;
  twclid?: string;
  epik?: string;
  tbclid?: string;
  obcid?: string;
  ob_click_id?: string;
  landing_page?: string;
  first_touch?: string;
  referrer?: string;
  raw_attribution?: string;
  // Seiten-Kontext der Website (08.10.2026), s. URL_SITE_CONTEXT_FIELDS.
  ls_campaign?: string;
  ls_context?: string;
  ls_page_type?: string;
  ls_placement?: string;
  ls_variant?: string;
  ls_locale?: string;
  ls_angle?: string;
  ls_creative?: string;
}

const ATTRIBUTION_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'oppref',
  'ttclid',
  'li_fat_id',
  'twclid',
  'epik',
  'tbclid',
  'obcid',
  'ob_click_id',
  'landing_page',
  'first_touch',
  'referrer',
] as const satisfies ReadonlyArray<keyof AttributionData>;

const URL_UTM_FIELDS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const satisfies ReadonlyArray<keyof AttributionData>;

// Ad click-IDs read straight from the URL as a fallback to the cookie.
// Janik 2026-06-24: click-IDs only ever came from the `leadscraper_ft`
// cookie, which lives on `.leadscraper.de` — a signup reached on another
// host (e.g. cortex.deimann.io) or linked directly with `?gclid=…` dropped
// the click-ID entirely. ~45% of signups were unattributed as a result.
const URL_CLICKID_FIELDS = [
  'gclid',
  'fbclid',
  'msclkid',
  'oppref',
  'ttclid',
  'li_fat_id',
  'twclid',
  'epik',
  'tbclid',
  'obcid',
  'ob_click_id',
] as const satisfies ReadonlyArray<keyof AttributionData>;

// Seiten-Kontext, den die Website an jeden Registrier-Link hängt: von welcher Art Seite
// (home, industry, article …), aus welchem Kontext und von welcher Platzierung der Klick kam,
// plus Kampagne und Variante des Angebotsblocks. Bisher blieb das in der URL liegen —
// organische Anmeldungen (ohne utm_*) kamen deshalb ganz ohne Herkunft am Konto an
// (08.10.2026: 17 von 18 PRISM-Anmeldungen „nicht zugeordnet“). Es sind Angaben über unsere
// eigene Seite, keine Kennung des Besuchers.
const URL_SITE_CONTEXT_FIELDS = [
  'ls_campaign',
  'ls_context',
  'ls_page_type',
  'ls_placement',
  'ls_variant',
  'ls_locale',
  'ls_angle',
  'ls_creative',
] as const satisfies ReadonlyArray<keyof AttributionData>;

/**
 * Reads the raw `leadscraper_ft` cookie value (URL-decoded). Returns `null`
 * when the cookie is missing or in an SSR context.
 */
export function getLeadscraperAttribution(): string | null {
  if (typeof document === 'undefined') return null;

  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const trimmed = cookie.trim();
    const eqIndex = trimmed.indexOf('=');
    if (eqIndex === -1) continue;
    const name = trimmed.substring(0, eqIndex);
    if (name !== 'leadscraper_ft') continue;
    const value = trimmed.substring(eqIndex + 1);
    try {
      return decodeURIComponent(value);
    } catch {
      // Malformed encoding — return raw so we still capture *something*.
      return value;
    }
  }
  return null;
}

/**
 * Parses a `leadscraper_ft` cookie value into a structured attribution
 * object. Supports both JSON-encoded payloads (newer landing pages) and
 * URL-encoded `key=value&...` strings (legacy).
 */
export function parseLeadscraperCookie(rawAttribution: string | null): AttributionData {
  if (!rawAttribution) return {};

  const attribution: AttributionData = { raw_attribution: rawAttribution };
  const trimmed = rawAttribution.trim();

  // JSON payload — the new landing pages stringify a JSON object.
  if (trimmed.startsWith('{')) {
    try {
      const json = JSON.parse(trimmed) as Record<string, unknown>;
      for (const key of ATTRIBUTION_FIELDS) {
        const v = json[key];
        if (v !== undefined && v !== null && v !== '') {
          attribution[key] = String(v);
        }
      }
      return attribution;
    } catch {
      // Fall through to URLSearchParams parsing.
    }
  }

  // Legacy URL-encoded format: utm_source=google&utm_medium=cpc&...
  try {
    const params = new URLSearchParams(trimmed);
    for (const key of ATTRIBUTION_FIELDS) {
      const v = params.get(key);
      if (v) attribution[key] = v;
    }
  } catch {
    // Keep raw_attribution only — backend can still inspect it.
  }
  return attribution;
}

/**
 * Convenience wrapper: read the cookie and parse it in one call.
 */
export function getStructuredAttribution(): AttributionData {
  return parseLeadscraperCookie(getLeadscraperAttribution());
}

/**
 * Reads UTM-style params from the current URL (`utm_source`, `utm_medium`,
 * `utm_campaign`, `utm_content`, `utm_term`).
 */
export function getUrlUtmParams(): Partial<AttributionData> {
  if (typeof window === 'undefined') return {};
  const out: Partial<AttributionData> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of URL_UTM_FIELDS) {
      const v = params.get(key);
      if (v) out[key] = v;
    }
  } catch {
    // window.location.search malformed — return what we have.
  }
  return out;
}

/**
 * Reads ad click-IDs (`gclid`, `fbclid`, `msclkid`, …) straight from the
 * current URL. Mirrors `getUrlUtmParams` but for click-IDs — see
 * `URL_CLICKID_FIELDS` for why: the `leadscraper_ft` cookie is the only
 * source today, and it doesn't travel across hosts.
 */
export function getUrlClickIds(): Partial<AttributionData> {
  if (typeof window === 'undefined') return {};
  const out: Partial<AttributionData> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of URL_CLICKID_FIELDS) {
      const v = params.get(key);
      if (v) out[key] = v;
    }
  } catch {
    // window.location.search malformed — return what we have.
  }
  return out;
}

/**
 * Reads the site-context params (`ls_page_type`, `ls_context`, …) the marketing site appends
 * to every register link. Values are short slugs; anything else is dropped.
 */
export function getUrlSiteContext(): Partial<AttributionData> {
  if (typeof window === 'undefined') return {};
  const out: Partial<AttributionData> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of URL_SITE_CONTEXT_FIELDS) {
      const v = params.get(key);
      if (v && /^[A-Za-z0-9_.-]{1,64}$/.test(v)) out[key] = v;
    }
  } catch {
    // window.location.search malformed — return what we have.
  }
  return out;
}

/**
 * Resolves the offer/source signal used by the Beta-Coupon flow.
 *
 * Priority order (matches old `register-page.tsx` behaviour):
 *   1. `?source` URL param
 *   2. `?offer` URL param
 *   3. `ls_offer` cookie (set by Webflow landing pages)
 *   4. `ls.offer` cookie (legacy spelling)
 *
 * Side-effect: when a value is found via URL or cookie we mirror it into
 * `sessionStorage` under the `ls_offer` key so subsequent navigations
 * within the SPA can reuse it (parity with old behaviour).
 */
// LEA-556: Der Kuendigen-Link aus der Trial-Erinnerungs-Mail (`/?cancel=1`).
//
// Warum das hier steht und nicht einfach in Account.jsx aus der URL gelesen
// wird: der Login-Bounce verwirft die URL ZWEIMAL. AuthPage replaceState't
// jeden Nicht-Auth-Pfad auf /login, bevor das Passwort getippt ist, und nach
// erfolgreichem Login macht main.tsx ein window.location.replace('/'). Wer den
// Param erst nach dem Login liest, sieht ihn nie — und Mail-Empfaenger sind
// ueberwiegend ausgeloggt. Also am allerersten Boot-Punkt sichern, genau wie
// getOfferSource() es fuer `?offer=` macht.
//
// Nicht hier aufraeumen: ein Reload vor dem Login wuerde das Flag sonst
// verlieren. Die URL saeubert der Consumer (BillingPanel), wenn er das Modal
// oeffnet.
export function captureCancelIntent(): boolean {
  return captureBillingIntent('cancel', 'ls_cancel_intent');
}

// LEA-574: Der Reaktivieren-Link aus der Kuendigungs-Mail (`/?reactivate=1`).
//
// Er war seit jeher tot: die Mail zeigte auf `/dashboard?reactivate=1`, aber
// `/dashboard` ist gar keine Route (die SPA waehlt ihren Screen aus
// sessionStorage), und `?reactivate` wurde von nichts gelesen. Wer nach der
// Kuendigung auf den einzigen CTA in unserer einzigen Mail klickte, landete
// irgendwo — haeufig im Assistenten — ohne jede Reaktivieren-Moeglichkeit.
export function captureReactivateIntent(): boolean {
  return captureBillingIntent('reactivate', 'ls_reactivate_intent');
}

// LEA-560: der Reaktivierungs-Link aus der Win-back-Mail (`/?winback=1`).
// Gleicher Mechanismus, gleicher Grund: der Login-Bounce verwirft die URL
// zweimal, Mail-Empfaenger sind ueberwiegend ausgeloggt.
export function captureWinbackIntent(): boolean {
  return captureBillingIntent('winback', 'ls_winback_intent');
}

// Gemeinsamer Kern beider Deep-Links. Bewusst hier und nicht im Consumer: der
// Login-Bounce verwirft die URL ZWEIMAL — AuthPage replaceState't jeden
// Nicht-Auth-Pfad auf /login, bevor das Passwort getippt ist, und nach dem
// Login macht main.tsx ein location.replace('/'). Mail-Empfaenger sind
// ueberwiegend ausgeloggt, also muss der Param am allerersten Boot-Punkt
// gesichert werden — genauso wie getOfferSource() es fuer `?offer=` macht.
//
// Nicht hier aufraeumen: ein Reload vor dem Login wuerde das Flag sonst
// verlieren. Die URL saeubert der Consumer, wenn er die Absicht einloest.
function captureBillingIntent(param: string, storageKey: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.get(param)) return false;
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // sessionStorage may be unavailable (private mode, embedded contexts) —
      // the deep link then simply lands on the app without auto-opening.
    }
    return true;
  } catch {
    return false;
  }
}

/** Gespeicherter Offer der Sitzung/des Cookies (ohne URL). */
function readStoredOffer(): string | null {
  // sessionStorage BEFORE cookie: der Mirror wird beim Boot aus einem
  // expliziten ?offer=-Param dieser Sitzung befüllt (bootApp ruft uns vor dem
  // Logged-out-Redirect, der die Query verwirft). Ohne diesen Read war der
  // Mirror eine Einbahnstraße — Root-Link-Signups kamen mit source:null an
  // (Staging-Befund 19.07.), weil nur URL und Cookie gelesen wurden. Ein
  // Session-expliziter Param schlägt einen potenziell alten GTM-Cookie —
  // es sei denn, er ist unbekannt und der Cookie kennt einen gültigen Offer.
  let fromSession: string | null = null;
  try {
    const stored = sessionStorage.getItem('ls_offer');
    if (stored && stored !== '__default__') fromSession = stored;
  } catch {
    // sessionStorage may be unavailable (private mode, embedded contexts).
  }
  if (fromSession && resolveOffer(fromSession)) return fromSession;

  let fromCookie: string | null = null;
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(';').map((c) => c.trim());
    const offerCookie =
      cookies.find((c) => c.startsWith('ls_offer=')) ??
      cookies.find((c) => c.startsWith('ls.offer='));
    if (offerCookie) {
      const eqIndex = offerCookie.indexOf('=');
      let offerValue: string;
      try {
        offerValue = decodeURIComponent(offerCookie.substring(eqIndex + 1));
      } catch {
        offerValue = offerCookie.substring(eqIndex + 1);
      }
      // The Webflow JS occasionally writes the literal sentinel `__default__`
      // when no offer applies — treat it as absence.
      if (offerValue && offerValue !== '__default__') fromCookie = offerValue;
    }
  }
  if (fromCookie && (resolveOffer(fromCookie) || !fromSession)) {
    rememberOffer(fromCookie);
    return fromCookie;
  }
  return fromSession;
}

function rememberOffer(value: string): void {
  try {
    sessionStorage.setItem('ls_offer', value);
  } catch {
    // sessionStorage may be unavailable (private mode, embedded contexts).
  }
}

export function getOfferSource(): string | null {
  if (typeof window === 'undefined') return null;

  let urlPick: string | null = null;
  try {
    const params = new URLSearchParams(window.location.search);
    const source = params.get('source');
    const offer = params.get('offer');
    // Ein unbekannter ?source= (z. B. ?source=google aus einem Newsletter) darf ein
    // bekanntes ?offer= nicht überstimmen: der Server löst `attribution.source` gegen die
    // Offer-Liste auf, unbekannt = „kein Offer“ = Sofort-Bezahl-Wand (Prüfbericht
    // 29.09.2026, Bericht 1, A4). Sind beide unbekannt, bleibt es beim bisherigen Verhalten.
    urlPick = resolveOffer(source) ? source : resolveOffer(offer) ? offer : (source ?? offer);
  } catch {
    // Unparseable search string — fall through to sessionStorage/cookie.
  }

  if (urlPick && resolveOffer(urlPick)) {
    rememberOffer(urlPick);
    return urlPick;
  }

  // Unbekannter URL-Wert: ein bereits gespeicherter, bekannter Offer (GTM-Cookie, Boot-Mirror)
  // gewinnt, sonst würde er stumm auf „kein Offer“ degradiert.
  const stored = readStoredOffer();
  if (stored && resolveOffer(stored)) return stored;

  if (urlPick) {
    rememberOffer(urlPick);
    return urlPick;
  }
  return stored ?? DEFAULT_SIGNUP_OFFER;
}

/**
 * Clears the `ls_offer` cookie and sessionStorage entry so the offer
 * source cannot leak to a subsequent signup on the same browser.
 *
 * LEA-266: without this, a user can log out → re-signup with a new
 * email and the stale cookie re-applies the trial offer, allowing
 * unlimited trial credit abuse.
 *
 * The `ls_offer` cookie is set by GTM on `.leadscraper.de` (parent
 * domain). A bare `document.cookie = 'ls_offer=; Max-Age=0; Path=/'`
 * only clears on the current subdomain — it can't reach the parent.
 * We walk up every domain level so the cookie is nuked regardless of
 * which level GTM originally scoped it to.
 *
 * Called after successful signup (source consumed) and on logout
 * (defense-in-depth).
 */
export function clearOfferSource(): void {
  if (typeof document !== 'undefined') {
    const names = ['ls_offer', 'ls.offer'];
    const expiry = 'Max-Age=0; Path=/';

    for (const name of names) {
      // Current-domain (implicit) — covers cookies set without explicit domain.
      document.cookie = `${name}=; ${expiry}`;

      // Walk up parent domains so we hit the GTM-scoped `.leadscraper.de` cookie.
      const parts = window.location.hostname.split('.');
      for (let i = 0; i < parts.length - 1; i++) {
        const domain = parts.slice(i).join('.');
        document.cookie = `${name}=; ${expiry}; domain=.${domain}`;
      }
    }
  }
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem('ls_offer');
    } catch {
      // sessionStorage may be unavailable.
    }
  }
}

/**
 * Aggregate everything the signup form needs in one call.
 *
 * Returns the exact shape the backend expects on `POST /api/auth/signup`
 * as `attribution: { ft, structured, source }`. URL UTM params AND ad
 * click-IDs are folded into `structured` for keys the cookie did not provide,
 * so a fresh visit with `?utm_source=foo` or `?gclid=…` is captured even
 * without the landing-page cookie (the cookie can't cross hosts).
 *
 * Fresh browser visits without an offer return `{ ft: null, structured: {},
 * source: 'prism' }`. Campaign overrides and first-touch attribution stay intact.
 */
export function captureSignupAttribution(): {
  ft: string | null;
  structured: AttributionData;
  source: string | null;
} {
  const ft = getLeadscraperAttribution();
  const structured = parseLeadscraperCookie(ft);
  const urlUtm = getUrlUtmParams();
  for (const key of URL_UTM_FIELDS) {
    const v = urlUtm[key];
    if (v && !structured[key]) structured[key] = v;
  }
  const urlClickIds = getUrlClickIds();
  for (const key of URL_CLICKID_FIELDS) {
    const v = urlClickIds[key];
    if (v && !structured[key]) structured[key] = v;
  }
  // Seiten-Kontext der Website: der Klick, der zur Registrierung führte, zählt.
  const siteContext = getUrlSiteContext();
  for (const key of URL_SITE_CONTEXT_FIELDS) {
    const v = siteContext[key];
    if (v) structured[key] = v;
  }
  return {
    ft,
    structured,
    source: getOfferSource(),
  };
}
